import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AidTypeInfo, BeneficiarySummary, Paginated } from '@foundation/shared';
import { CASE_PRIORITIES, CASE_PRIORITY_LABEL_TH, REQUEST_CHANNELS, REQUEST_CHANNEL_LABEL_TH } from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { formatSatang } from '../lib/format';
import { ErrorAlert } from '../components/ui';

export default function CaseCreate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [aidTypes, setAidTypes] = useState<AidTypeInfo[]>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<BeneficiarySummary[]>([]);
  const [picked, setPicked] = useState<BeneficiarySummary | null>(null);

  const [aidTypeId, setAidTypeId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<(typeof CASE_PRIORITIES)[number]>('normal');
  const [requestChannel, setRequestChannel] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');

  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<AidTypeInfo[]>('/meta/aid-types')
      .then(setAidTypes)
      .catch(() => setAidTypes([]));
  }, []);

  // มาจากหน้าผู้รับความช่วยเหลือ — เติมให้เลย ไม่ต้องค้นหาซ้ำ
  useEffect(() => {
    const id = params.get('beneficiaryId');
    if (!id) return;
    api<BeneficiarySummary>(`/beneficiaries/${id}`)
      .then(setPicked)
      .catch(() => undefined);
  }, [params]);

  useEffect(() => {
    if (!search.trim() || picked) return;
    const t = setTimeout(() => {
      api<Paginated<BeneficiarySummary>>(`/beneficiaries${buildQuery({ q: search, limit: 10 })}`)
        .then((r) => setResults(r.data))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [search, picked]);

  const selected = aidTypes.find((a) => a.id === aidTypeId);
  const satang = Math.round(Number(amount || '0') * 100);
  // แสดงให้เห็นก่อนกดว่าต้องใช้ผู้อนุมัติระดับไหน จะได้ไม่ยื่นแล้วค้าง
  const level =
    selected && selected.maxAmount !== null && satang > selected.maxAmount
      ? Math.min(3, selected.approvalLevel + 1)
      : (selected?.approvalLevel ?? 1);

  async function submit() {
    if (!picked || !aidTypeId || !title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ id: string }>('/cases', {
        method: 'POST',
        body: {
          beneficiaryId: picked.id,
          aidTypeId,
          title: title.trim(),
          description: description.trim() || undefined,
          priority,
          requestChannel: requestChannel || undefined,
          requestedAmount: satang > 0 ? satang : undefined,
          dueDate: dueDate || undefined,
        },
        idempotent: true,
      });
      navigate(`/cases/${res.id}`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>เปิดเคสความช่วยเหลือ</h1>
          <p>เคสจะเริ่มที่สถานะฉบับร่าง แก้ไขได้จนกว่าจะยื่นขออนุมัติ</p>
        </div>
      </div>

      <ErrorAlert error={error} />

      <div className="card">
        <h2>ผู้รับความช่วยเหลือ</h2>
        {picked ? (
          <div className="btn-row">
            <div>
              <strong>
                {picked.firstName} {picked.lastName}
              </strong>
              <div className="muted small">
                {picked.benCode}
                {picked.areaName ? ` · ${picked.areaName}` : ''}
              </div>
            </div>
            <button className="btn ghost" onClick={() => setPicked(null)}>
              เปลี่ยน
            </button>
          </div>
        ) : (
          <>
            <div className="field">
              <label htmlFor="bsearch">ค้นหา</label>
              <input
                id="bsearch"
                type="search"
                placeholder="ชื่อ รหัส หรือเบอร์โทร"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {results.length > 0 && (
              <div className="table-wrap">
                <table>
                  <tbody>
                    {results.map((b) => (
                      <tr key={b.id}>
                        <td>
                          {b.firstName} {b.lastName}
                          <div className="muted small">
                            {b.benCode}
                            {b.areaName ? ` · ${b.areaName}` : ''}
                          </div>
                        </td>
                        <td>
                          <button className="btn ghost" onClick={() => setPicked(b)}>
                            เลือก
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <h2>รายละเอียดคำขอ</h2>

        <div className="form-row">
          <div className="field">
            <label htmlFor="atype">ประเภทความช่วยเหลือ</label>
            <select id="atype" value={aidTypeId} onChange={(e) => setAidTypeId(e.target.value)}>
              <option value="">เลือก…</option>
              {aidTypes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cprio">ความเร่งด่วน</label>
            <select
              id="cprio"
              value={priority}
              onChange={(e) => setPriority(e.target.value as typeof priority)}
            >
              {CASE_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {CASE_PRIORITY_LABEL_TH[p]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label htmlFor="ctitle">เรื่อง</label>
          <input
            id="ctitle"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="เช่น ค่าครองชีพฉุกเฉินหลังบ้านถูกน้ำท่วม"
          />
        </div>

        <div className="field">
          <label htmlFor="cdesc">รายละเอียด</label>
          <textarea
            id="cdesc"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="camt">ยอดที่ขอ (บาท)</label>
            <input
              id="camt"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={selected?.defaultAmount ? String(selected.defaultAmount / 100) : ''}
            />
            {selected && (
              <p className="hint">
                วงเงินปกติของประเภทนี้ {formatSatang(selected.maxAmount)} · ต้องอนุมัติโดยผู้มีอำนาจ
                ระดับ {level}
                {level > selected.approvalLevel && ' (ยกระดับเพราะเกินวงเงิน)'}
              </p>
            )}
          </div>
          <div className="field">
            <label htmlFor="cdue">กำหนดเสร็จ</label>
            <input id="cdue" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="cchan">ช่องทางที่รับเรื่อง</label>
            <select
              id="cchan"
              value={requestChannel}
              onChange={(e) => setRequestChannel(e.target.value)}
            >
              <option value="">ไม่ระบุ</option>
              {REQUEST_CHANNELS.map((r) => (
                <option key={r} value={r}>
                  {REQUEST_CHANNEL_LABEL_TH[r]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="btn-row">
          <button
            className="btn"
            disabled={busy || !picked || !aidTypeId || !title.trim()}
            onClick={() => void submit()}
          >
            เปิดเคส
          </button>
          <button className="btn ghost" onClick={() => navigate('/cases')}>
            ยกเลิก
          </button>
        </div>
      </div>
    </>
  );
}
